<template>
  <PageLayout>
    <template slot="main">
      <div class="initial-page">
        <b-container>
          <b-row v-if="canAccessNewEngagement">
            <b-col class="new-engagement-redirect">
              <a @click="navigateNewEngagement()">
                <div class="new-engagement-redirect-message-container">
                  <span class="new-engagement-redirect-message">
                    {{ initialResources.newEngagement.message1 }}
                    <span class="new-engagement-redirect-message-strong">
                      {{ initialResources.newEngagement.message2 }} </span
                    >.
                    <span class="new-engagement-redirect-message-strong">
                      {{ initialResources.newEngagement.message3 }}
                    </span>
                    {{ initialResources.newEngagement.message4 }}
                  </span>
                </div>
              </a>
            </b-col>
          </b-row>
          <b-row v-if="canAccessNewProfile">
            <b-col class="new-engagement-redirect">
              <a @click="navigateNewProfile()">
                <div class="new-engagement-redirect-message-container">
                  <span class="new-engagement-redirect-message">
                    {{ initialResources.newLayout.message1 }}
                    <span class="new-engagement-redirect-message-strong">
                      {{ initialResources.newLayout.message2 }}
                    </span>
                    {{ initialResources.newLayout.message3 }}
                  </span>
                </div>
              </a>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <header class="col mt-3 mb-4 p-0">
                <b-row>
                  <b-col cols="10">
                    <p class="m-0">
                      {{ salutation }} {{ user.profile.firstname || '' }}!
                    </p>
                    <h3 class="medium m-0">
                      {{ initialResources.description }}
                    </h3>
                  </b-col>
                </b-row>
              </header>
              <b-row>
                <can I="use" a="engagement">
                  <!-- SURVEY -->
                  <DashboardCard
                    v-if="!$can('access', 'read-only')"
                    :title="dashboardCardsResources.survey.title"
                    :subTitle="dashboardCardsResources.survey.subTitle"
                    color="tertiary"
                    :route="{ name: 'survey.begin' }"
                    img="question"
                  >
                  </DashboardCard>
                  <!-- REPORTS -->
                  <can I="see" a="menu.reports">
                    <DashboardCard
                      :title="dashboardCardsResources.report.title"
                      :subTitle="dashboardCardsResources.report.subTitle"
                      color="lima"
                      img="report"
                      :route="{
                        name: 'reports',
                        params: { groupId: 'company' },
                      }"
                    >
                    </DashboardCard>
                  </can>
                </can>
                <!-- TICKETS -->
                <DashboardCard
                  v-if="!$can('access', 'read-only')"
                  :title="dashboardCardsResources.feedbackTickets.title"
                  :subTitle="dashboardCardsResources.feedbackTickets.subTitle"
                  color="blue-water"
                  :route="{ name: 'feedbacks.tickets' }"
                  img="feedback"
                >
                </DashboardCard>
                <!-- USERS -->
                <can I="access" a="page.users">
                  <DashboardCard
                    :title="dashboardCardsResources.dashboardUsers.title"
                    :subTitle="dashboardCardsResources.dashboardUsers.subTitle"
                    color="quinary"
                    :route="{ name: 'dashboard.users' }"
                    img="user"
                  >
                  </DashboardCard>
                </can>
                <!-- SEND TICKET -->
                <DashboardCard
                  v-if="!$can('access', 'read-only')"
                  :title="dashboardCardsResources.newFeedbackTickets.title"
                  :subTitle="
                    dashboardCardsResources.newFeedbackTickets.subTitle
                  "
                  color="red"
                  img="send-feedback"
                  :route="{
                    name: 'feedbacks.tickets.open',
                    params: { ticketId: 'new' },
                  }"
                  textColor="text-white"
                >
                </DashboardCard>
                <can I="use" a="engagement">
                  <!-- CUSTOM POLL -->
                  <!-- <DashboardCard
                    v-if="!$can('access', 'read-only')"
                    :title="dashboardCardsResources.customPoll.title"
                    img="custom-poll"
                    :subTitle="dashboardCardsResources.customPoll.subTitle"
                    color="orange01"
                    :route="{ name: 'customPoll' }"
                  >
                  </DashboardCard> -->
                </can>
                <!-- CUSTOM SURVEY -->
                <DashboardCard
                  v-if="!$can('access', 'read-only')"
                  :title="dashboardCardsResources.customSurvey.title"
                  img="custom-poll"
                  :subTitle="dashboardCardsResources.customSurvey.subTitle"
                  color="purple01"
                  textColor="text-white"
                  :route="{ name: 'custom-surveys' }"
                >
                </DashboardCard>
                <!-- GROUPS -->
                <can I="see" a="menu.groups">
                  <DashboardCard
                    :title="dashboardCardsResources.groups.title"
                    img="group"
                    :subTitle="dashboardCardsResources.groups.subTitle"
                    color="purple04"
                    textColor="text-white"
                    :route="{ name: 'dashboard.groups' }"
                  >
                  </DashboardCard>
                </can>
              </b-row>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import PageLayout from '@/components/Home/PageLayout.vue'
import DashboardCard from '@/components/Home/Widgets/DashboardCard.vue'
import {
  getUser,
  isEnterprisePlan,
  isAdminOrManager,
  isEarlyAccessTrialAccount,
  generateCookie,
} from '@/service/user'
import { ifElse, flip, always, prop, assoc, clone } from 'ramda'
import { tourInstance } from '@/service/tour'

export default {
  components: {
    PageLayout,
    DashboardCard,
  },

  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'dashboard' })

    const tour = this.resources.tours[0]
    const { id: tourId } = tour

    const tgSetup = clone(this.resources.tourGuideSetup)
    const tg = tourInstance({
      ...tgSetup,
      tours: clone(this.resources.tours),
      hidePrev: true,
      showStepProgress: false,
      showStepDots: false,
    })

    if (!tg.isFinished(tourId) && isAdminOrManager()) {
      tg.start(tourId)
    }
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      message: 'reports/message',
    }),
    initialResources: function() {
      return this.resources.page.home.initial
    },
    salutationResources: function() {
      return this.initialResources.salutation
    },
    dashboardCardsResources: function() {
      const _prop = flip(prop)
      const _customPoll = _prop(this.initialResources.dashboardCards.customPoll)
      const customPoll = ifElse(
        always(this.$can('create', 'customPoll')),
        always(_customPoll('manager')),
        always(_customPoll('user'))
      )()

      return assoc(
        'customPoll',
        customPoll,
        this.initialResources.dashboardCards
      )
    },
    salutation: function() {
      if (new Date().getHours() < 12)
        return this.salutationResources.goodMorning
      else if (new Date().getHours() < 18)
        return this.salutationResources.goodAfternoon
      else return this.salutationResources.goodEvening
    },
    user() {
      return getUser()
    },
    canAccessNewEngagement() {
      return (
        (isEnterprisePlan() || isEarlyAccessTrialAccount()) &&
        isAdminOrManager() &&
        this.$can('use', 'engagement') &&
        !this.$can('access', 'read-only')
      )
    },
    canAccessNewProfile() {
      return (
        (isEnterprisePlan() || isEarlyAccessTrialAccount()) &&
        (!this.$can('use', 'engagement') || !isAdminOrManager()) &&
        !this.$can('access', 'read-only')
      )
    },
  },
  beforeDestroy() {
    this.$root.$emit('app:message:hideOnBeforeDestroy')
    this.$store.commit('reports/hideMessage')
  },
  methods: {
    async navigateNewEngagement() {
      await generateCookie()

      const env = window.env || process.env
      const host = env.VUE_APP_NEW_ENGAGEMENT_HOST

      window.location = `${host}/climate?l=${localStorage.languageabrev}`
    },
    async navigateNewProfile() {
      await generateCookie()

      const env = window.env || process.env
      const host = env.VUE_APP_NEW_PROFILE_HOST

      window.location = `${host}/home?l=${localStorage.languageabrev}`
    },
  },
}
</script>

<style lang="scss">
.slider.slider-horizontal {
  width: 100%;
}
</style>

<style scoped lang="scss">
.card {
  max-width: 20px;
}

.container-flex {
  display: flex;
  flex-wrap: wrap;
}

.initial-page,
a {
  background-repeat: no-repeat;
  background-position: top;
  color: var(--primary);
  text-decoration: none;

  p {
    color: #707070;
    font-size: 20px;
  }

  @media (max-width: 425px) {
    p {
      font-size: 18px;
    }

    h3 {
      font-size: 22px;
    }
  }
}

.new-engagement-redirect {
  text-align: center;
  min-height: 45px;

  a {
    cursor: pointer;
  }

  .new-engagement-redirect-message-container {
    background-color: #ffba33;
    border-radius: 5px;
    min-height: 45px;

    .new-engagement-redirect-message {
      font-size: 13px;
      font-weight: 600;
      margin: 15px;
      display: inline-table;

      .new-engagement-redirect-message-strong {
        font-weight: 800;
      }
    }
  }
}
</style>
